<script>
import axios from "axios";
import ShipmentBatchPreview from "./ShipmentBatchPreview.vue";

// Local YYYY-MM-DD for "today" without UTC shift (the API expects ISO dates).
function todayISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export default {
  name: "CityExpressOtpremnicaList",
  components: { ShipmentBatchPreview },
  props: {
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: false,
      mode: "range", // since-last | range | all
      from: todayISO(),
      to: todayISO(),
      otpremnice: [],
      lastShippedOtpremnicaId: null,
      // We keep a Set for O(1) membership and a counter to drive Vue
      // reactivity (Vue 2 cannot observe Set mutations directly).
      selectedSet: new Set(),
      selectedVersion: 0,
      // Client-side pagination: rendering thousands of <tr> at once is the
      // remaining bottleneck once payload is slim. Show a page at a time.
      currentPage: 1,
      perPage: 100,
      perPageOptions: [25, 50, 100, 200],
      filterText: "",
      // Feature B: otpremnice already put on a (non-cancelled) CE shipment are
      // hidden by default so the operator only sees what's left to ship.
      showShipped: false,
      showPreview: false,
      previewOtpremnice: [],
    };
  },
  computed: {
    fields() {
      return [
        { key: "selected", label: "" },
        { key: "id", label: "ID" },
        { key: "broj", label: "Broj otpremnice" },
        { key: "datum", label: "Datum" },
        { key: "kupac", label: "Kupac" },
        { key: "objekat", label: "Objekat" },
        { key: "brojProfakture", label: "Profaktura" },
        { key: "brojPaketa", label: "Pak." },
        { key: "vrednost", label: "Ukupno", class: "text-right" },
      ];
    },
    selectedCount() {
      // Touch the version so Vue tracks this getter.
      // eslint-disable-next-line no-unused-expressions
      this.selectedVersion;
      return this.selectedSet.size;
    },
    canPrepare() {
      return this.selectedCount > 0;
    },
    filteredRows() {
      let rows = this.otpremnice;
      if (!this.showShipped) {
        rows = rows.filter((r) => !r.shipped);
      }
      const q = this.filterText.trim().toLowerCase();
      if (!q) return rows;
      return rows.filter((r) => {
        return (
          (r.kupac &&
            String(r.kupac)
              .toLowerCase()
              .includes(q)) ||
          (r.objekat &&
            String(r.objekat)
              .toLowerCase()
              .includes(q)) ||
          (r.brojProfakture &&
            String(r.brojProfakture)
              .toLowerCase()
              .includes(q)) ||
          (r.broj &&
            String(r.broj)
              .toLowerCase()
              .includes(q)) ||
          (r.id &&
            String(r.id)
              .toLowerCase()
              .includes(q))
        );
      });
    },
    totalRows() {
      return this.filteredRows.length;
    },
    // Rows the operator can actually select for a new shipment: already-shipped
    // otpremnice are excluded so they can't be re-shipped by accident.
    selectableRows() {
      return this.filteredRows.filter((r) => !r.shipped);
    },
    // How many rows are hidden purely because they're already shipped.
    shippedHiddenCount() {
      if (this.showShipped) return 0;
      let n = 0;
      for (const r of this.otpremnice) if (r.shipped) n++;
      return n;
    },
    allFilteredSelected() {
      // eslint-disable-next-line no-unused-expressions
      this.selectedVersion;
      const rows = this.selectableRows;
      if (rows.length === 0) return false;
      for (const o of rows) {
        if (!this.selectedSet.has(o.id)) return false;
      }
      return true;
    },
  },
  watch: {
    active(isActive, wasActive) {
      if (isActive && wasActive === false) this.load();
    },
    filterText() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    },
    showShipped() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async load(fresh = false) {
      this.loading = true;
      try {
        const params = { mode: this.mode };
        if (fresh) params.fresh = 1;
        if (this.mode === "range") {
          if (this.from) params.from = this.from;
          if (this.to) params.to = this.to;
        }
        const { data } = await axios.get(
          "/super-admin/city-express/otpremnice",
          { params },
        );
        const payload = data.data || {};
        this.otpremnice = Object.freeze(payload.otpremnice || []);
        this.lastShippedOtpremnicaId = payload.lastShippedOtpremnicaId ?? null;

        // Drop selections that are no longer visible.
        const visibleIds = new Set(this.otpremnice.map((o) => o.id));
        const next = new Set();
        for (const id of this.selectedSet) {
          if (visibleIds.has(id)) next.add(id);
        }
        this.selectedSet = next;
        this.selectedVersion++;
      } finally {
        this.loading = false;
      }
    },
    isSelected(id) {
      // eslint-disable-next-line no-unused-expressions
      this.selectedVersion;
      return this.selectedSet.has(id);
    },
    toggle(id) {
      if (this.selectedSet.has(id)) this.selectedSet.delete(id);
      else this.selectedSet.add(id);
      this.selectedVersion++;
    },
    toggleAll() {
      // "Select all" applies to the current filter, not to the entire list,
      // and never touches already-shipped rows.
      const rows = this.selectableRows;
      if (this.allFilteredSelected) {
        for (const o of rows) this.selectedSet.delete(o.id);
      } else {
        for (const o of rows) this.selectedSet.add(o.id);
      }
      this.selectedVersion++;
    },
    openPreview() {
      if (!this.canPrepare) return;
      this.previewOtpremnice = this.otpremnice.filter((o) =>
        this.selectedSet.has(o.id),
      );
      this.showPreview = true;
    },
    onPreviewDone() {
      this.showPreview = false;
      this.previewOtpremnice = [];
      this.selectedSet = new Set();
      this.selectedVersion++;
      this.load();
    },
    onPreviewCancel() {
      this.showPreview = false;
      this.previewOtpremnice = [];
      // Refresh anyway: any successful sub-shipments are persisted.
      this.load();
    },
    formatMoney(v) {
      if (v === null || v === undefined || v === "") return "";
      return Number(v).toLocaleString("sr-RS", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(v) {
      if (!v) return "";
      // Wings returns ISO-ish strings ("YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss").
      // Take only the date part and re-emit as dd.mm.yyyy (EU), avoiding Date
      // parsing so we don't shift days due to timezone offsets.
      const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[3]}.${m[2]}.${m[1]}`;
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      return `${dd}.${mm}.${d.getFullYear()}`;
    },
  },
};
</script>

<template>
  <div>
    <div class="ce-toolbar mb-2">
      <div class="ce-toolbar__left">
        <b-input-group class="ce-search">
          <b-input-group-prepend is-text>
            <span aria-hidden="true">🔍</span>
          </b-input-group-prepend>
          <b-form-input
            v-model="filterText"
            placeholder="Pretraga (kupac, objekat, profaktura, broj, ID)"
            debounce="200"
          />
          <b-input-group-append v-if="filterText">
            <b-button variant="outline-secondary" @click="filterText = ''">
              ×
            </b-button>
          </b-input-group-append>
        </b-input-group>

        <div>
          <label class="mb-1">Prikaz</label>
          <b-form-select
            v-model="mode"
            @change="load()"
            style="min-width: 200px"
          >
            <option value="since-last">Od poslednje poslate</option>
            <option value="range">Opseg datuma</option>
            <option value="all">Sve</option>
          </b-form-select>
        </div>

        <div class="d-flex align-items-end" style="padding-bottom: 0.35rem">
          <b-form-checkbox v-model="showShipped" switch>
            Prikaži i poslate
            <b-badge
              v-if="shippedHiddenCount > 0"
              variant="secondary"
              class="ml-1"
            >
              {{ shippedHiddenCount }}
            </b-badge>
          </b-form-checkbox>
        </div>

        <div
          v-if="mode === 'range'"
          class="ce-dates d-flex align-items-end"
          style="gap: 0.5rem"
        >
          <div>
            <label class="mb-1">Od</label>
            <b-form-datepicker
              v-model="from"
              locale="sr"
              :start-weekday="1"
              :date-format-options="{
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              }"
              placeholder="Od"
              today-button
              reset-button
              close-button
              label-today-button="Danas"
              label-reset-button="Poništi"
              label-close-button="Zatvori"
              style="min-width: 180px"
              @input="load()"
            />
          </div>
          <div>
            <label class="mb-1">Do</label>
            <b-form-datepicker
              v-model="to"
              locale="sr"
              :start-weekday="1"
              :date-format-options="{
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              }"
              placeholder="Do"
              today-button
              reset-button
              close-button
              label-today-button="Danas"
              label-reset-button="Poništi"
              label-close-button="Zatvori"
              style="min-width: 180px"
              @input="load()"
            />
          </div>
        </div>
      </div>

      <div class="ce-toolbar__right">
        <div class="text-right">
          <small class="text-muted d-block mb-1">
            Poslednja poslata otpremnica:
            {{ lastShippedOtpremnicaId ? "O" + lastShippedOtpremnicaId : "—" }}
          </small>
          <b-button
            variant="outline-secondary"
            class="mr-2"
            :disabled="loading"
            @click="load(true)"
            title="Povuci sveže podatke iz Wings-a (zaobiđi keš)"
          >
            <b-spinner v-if="loading" small class="mr-1" />
            Osveži podatke
          </b-button>
          <b-button
            variant="primary"
            :disabled="!canPrepare"
            @click="openPreview()"
          >
            Pripremi pošiljke ({{ selectedCount }})
          </b-button>
        </div>
      </div>
    </div>

    <b-table
      :items="filteredRows"
      :fields="fields"
      :busy="loading"
      :per-page="perPage"
      :current-page="currentPage"
      small
      striped
      hover
      no-local-sorting
      no-provider-paging
      no-provider-filtering
    >
      <template #head(selected)>
        <b-form-checkbox :checked="allFilteredSelected" @change="toggleAll()" />
      </template>
      <template #cell(selected)="row">
        <b-form-checkbox
          :checked="isSelected(row.item.id)"
          :disabled="row.item.shipped"
          @change="toggle(row.item.id)"
        />
      </template>
      <template #cell(id)="row">
        {{ row.item.id }}
        <b-badge
          v-if="row.item.shipped"
          variant="success"
          class="ml-1"
          :title="
            'Već poslato — status pošiljke: ' + (row.item.shipment_status || '')
          "
        >
          Poslato
        </b-badge>
      </template>
      <template #cell(datum)="row">
        {{ formatDate(row.item.datum) }}
      </template>
      <template #cell(vrednost)="row">
        {{ formatMoney(row.item.vrednost) }}
      </template>
      <template #empty>
        <div class="text-center text-muted py-3">
          Nema otpremnica za prikaz.
        </div>
      </template>
      <template #table-busy>
        <div class="text-center py-3"><b-spinner small /> Učitavanje…</div>
      </template>
    </b-table>

    <div
      class="ce-pager d-flex justify-content-between align-items-center mt-2"
    >
      <small class="text-muted">
        Ukupno: {{ totalRows }} · Izabrano: {{ selectedCount }}
      </small>
      <div class="d-flex align-items-center" style="gap: 0.5rem">
        <label class="mb-0 small text-muted">Po strani</label>
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          size="sm"
          style="width: 90px"
        />
        <b-pagination
          v-if="totalRows > perPage"
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          size="sm"
          class="mb-0"
        />
      </div>
    </div>

    <b-modal
      v-model="showPreview"
      title="Pripremi City Express pošiljke"
      hide-footer
      size="xl"
      no-close-on-backdrop
      no-close-on-esc
    >
      <shipment-batch-preview
        v-if="showPreview"
        :otpremnice="previewOtpremnice"
        @done="onPreviewDone"
        @cancel="onPreviewCancel"
      />
    </b-modal>
  </div>
</template>

<style scoped>
.ce-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.ce-toolbar__left {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.5rem;
  flex: 1 1 auto;
}
.ce-toolbar__right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-left: 0.75rem;
  border-left: 1px solid #e5e7eb;
}
.ce-search {
  min-width: 260px;
  max-width: 360px;
  flex: 1 1 260px;
}
.ce-dates {
  width: auto;
  flex: 0 0 auto;
}
</style>
